import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { useMutation, useQueryClient } from "react-query";

import { apollo } from "../../../../utils/apollo";
import { IMPORT_COC_FILE } from "../../../../utils/graphql";
import { ArrowDownTrayIcon, TrashIcon } from "@heroicons/react/24/solid";
import ButtonLink from "../../../../components/ButtonLink";
import Loading from "../../../../components/Loading";
import { cn } from "../../../../utils/style";
import { toast } from "react-toastify";
import { http } from "../../../../utils/http";
import { GAID_API_URL, GAID_API_BASE_URL } from "../../../../utils/env";
import Button from "../../../../components/Button";
import FileUpload from "../../../../components/FileUpload";
function ShafafiyaPolicyImportForm({ integration, onSuccess }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [uploading, setUploading] = useState(false);
  const [submitAtOnce, setSubmitAtOnce] = useState(false);
  const [errorLines, setErrorLines] = useState([]);
  const [file, setFile] = useState({
    realFileName: null,
    fileName: null,
    fileUrl: null,
    mimeType: null,
  });
  const importCocFileMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apollo.mutate({
        mutation: IMPORT_COC_FILE,
        variables: {
          input: {
            integrationUuid: integration?.integrationUuid || null,
            fileUrl: file?.fileUrl || null,
            mimeType: file?.mimeType || null,
            submitAtOnce: submitAtOnce,
          },
        },
      });
      return data?.importCOCFile;
    },
    onSuccess: (data) => {
      if (data.success && !data?.errorLines?.length) {
        toast.success("COC file is being processed...");
        queryClient.invalidateQueries(["policy_list"]);
        if (onSuccess) onSuccess(data.fileImportUuid);
      }
      if (data.success && !!data?.errorLines?.length) {
        toast.error("Some records could not be processed successfully.");
        setErrorLines(data?.errorLines);
      }
    },
    onError: (error) => {
      console.error(error);
      toast.error("An error occurred while initiating the import.");
    },
  });
  const resetFileField = () => {
    setFile({
      realFileName: null,
      fileName: null,
      fileUrl: null,
      mimeType: null,
    });
    setErrorLines([]);
  };
  const handleFileUpload = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const response = await http.post("/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      const { fileName, fileUrl, mimeType } = response?.data;
      if (fileName) {
        setFile({
          fileName,
          fileUrl,
          mimeType,
        });
      }
    } catch (error) {
      console.error(error);
      toast.error("File upload failed");
    }
    setUploading(false);
  };
  return (
    <div>
      <div className="flex flex-col md:flex-row gap-4 my-4 border border-sky-200 bg-sky-50 p-4 rounded">
        <div className="text-sm text-sky-800">
          {t(
            "Download the member data template, fill the details and re-upload it. Please do not change the name and\n                format of the downloaded file while uploading it. Please use latest XLSX template while uploading the\n                data",
          )}
        </div>
        <div className="shrink-0">
          <ButtonLink
            href={`${GAID_API_BASE_URL}/shafafiya_template.xlsx`}
            target="_blank"
            secondary={true}
          >
            {t("Download shafafiya.xlsx")}
          </ButtonLink>
        </div>
      </div>

      <div
        className={cn("sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-8 sm:grid")}
      >
        <label className="block text-sm font-black uppercase tracking-widest text-slate-500 sm:pt-1.5">
          {t("CSV/XLSX File")}
          <span className="text-red-500">*</span>
        </label>
        <div className="relative mt-2 sm:col-span-2 sm:mt-0">
          <FileUpload
            id="coc-bulk-upload-file"
            name="coc-bulk-upload-file"
            onChange={handleFileUpload}
            fileName={file?.fileName}
            onClear={resetFileField}
            disabled={uploading}
          />

          {uploading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[2px] rounded-2xl">
              <Loading message="Uploading..." />
            </div>
          )}

          {file?.fileUrl && (
            <div className="mt-4 flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
              <ButtonLink
                href={`${GAID_API_URL}${file?.fileUrl}`}
                target="_blank"
                ghost
                className="flex items-center gap-2 !text-xs !py-2"
              >
                <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                <span>{t("Preview File")}</span>
              </ButtonLink>
            </div>
          )}
        </div>
      </div>

      <div className="sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 sm:grid border-t border-slate-100">
        <label className="block text-sm font-black uppercase tracking-widest text-slate-500 sm:pt-1.5">
          {t("Submission Options")}
        </label>
        <div className="sm:col-span-2">
          <div className="flex items-start gap-3">
            <div className="flex items-center h-5">
              <input
                id="submit-at-once"
                type="checkbox"
                checked={submitAtOnce}
                onChange={(e) => setSubmitAtOnce(e.target.checked)}
                className="h-5 w-5 rounded-lg border-slate-200 text-primary-600 focus:ring-primary-500 transition-all cursor-pointer"
              />
            </div>
            <div className="space-y-1">
              <label
                htmlFor="submit-at-once"
                className="text-sm text-slate-700 font-bold cursor-pointer select-none"
              >
                {t("Consolidated Batch Submission")}
              </label>
              <p className="text-xs text-slate-500 leading-relaxed max-w-lg">
                {t(
                  "When enabled, the system uses the same Shafafiya reference number for all members in the file. Disable this if each member has a unique transaction ID.",
                )}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
            <div
              className={cn(
                "transition-all duration-300",
                !submitAtOnce ? "opacity-40 grayscale" : "scale-[1.02]",
              )}
            >
              <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
                <div className="bg-slate-50 px-3 py-2 border-b border-slate-200 flex justify-between items-center">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    {t("Consolidated Mode")}
                  </span>
                  <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase">
                    {t("Single Ref")}
                  </span>
                </div>
                <div className="p-3">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="text-slate-400 border-b border-slate-100">
                        <th className="text-left pb-1 font-medium">{t("Row")}</th>
                        <th className="text-left pb-1 font-medium">
                          {t("Member")}
                        </th>
                        <th className="text-right pb-1 font-medium">
                          {t("Ref No")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      <tr>
                        <td className="py-1.5 text-slate-400">1</td>
                        <td className="py-1.5 font-medium text-slate-700">
                          John Doe
                        </td>
                        <td className="py-1.5 text-right font-mono font-bold text-primary-600">
                          REF-001
                        </td>
                      </tr>
                      <tr>
                        <td className="py-1.5 text-slate-400">2</td>
                        <td className="py-1.5 font-medium text-slate-700">
                          Jane Doe
                        </td>
                        <td className="py-1.5 text-right font-mono font-bold text-primary-600">
                          REF-001
                        </td>
                      </tr>
                      <tr>
                        <td className="py-1.5 text-slate-400">3</td>
                        <td className="py-1.5 font-medium text-slate-700">
                          Sam Smith
                        </td>
                        <td className="py-1.5 text-right font-mono font-bold text-primary-600">
                          REF-001
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <p className="mt-2 text-[10px] text-center text-slate-400 font-medium italic">
                {t("Same reference for all members")}
              </p>
            </div>

            <div
              className={cn(
                "transition-all duration-300",
                submitAtOnce ? "opacity-40 grayscale" : "scale-[1.02]",
              )}
            >
              <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
                <div className="bg-slate-50 px-3 py-2 border-b border-slate-200 flex justify-between items-center">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    {t("Individual Mode")}
                  </span>
                  <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase">
                    {t("Multi Ref")}
                  </span>
                </div>
                <div className="p-3">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="text-slate-400 border-b border-slate-100">
                        <th className="text-left pb-1 font-medium">{t("Row")}</th>
                        <th className="text-left pb-1 font-medium">
                          {t("Member")}
                        </th>
                        <th className="text-right pb-1 font-medium">
                          {t("Ref No")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      <tr>
                        <td className="py-1.5 text-slate-400">1</td>
                        <td className="py-1.5 font-medium text-slate-700">
                          John Doe
                        </td>
                        <td className="py-1.5 text-right font-mono font-bold text-primary-600">
                          REF-101
                        </td>
                      </tr>
                      <tr>
                        <td className="py-1.5 text-slate-400">2</td>
                        <td className="py-1.5 font-medium text-slate-700">
                          Jane Doe
                        </td>
                        <td className="py-1.5 text-right font-mono font-bold text-primary-600">
                          REF-102
                        </td>
                      </tr>
                      <tr>
                        <td className="py-1.5 text-slate-400">3</td>
                        <td className="py-1.5 font-medium text-slate-700">
                          Sam Smith
                        </td>
                        <td className="py-1.5 text-right font-mono font-bold text-primary-600">
                          REF-103
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <p className="mt-2 text-[10px] text-center text-slate-400 font-medium italic">
                {t("Unique reference per member")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {!!errorLines?.length && (
        <div className="mt-8 space-y-3">
          <label
            htmlFor="coc-bulk-errors"
            className="block text-sm font-black uppercase tracking-widest text-red-500"
          >
            {t("Error Lines Detected")}
          </label>
          <textarea
            name="coc-bulk-errors"
            id="coc-bulk-errors"
            rows="6"
            className="block w-full rounded-2xl border-slate-200 bg-slate-50 font-mono text-xs text-red-600 shadow-sm focus:border-red-500 focus:ring-red-500 transition-all"
            value={errorLines.join("\n")}
            readOnly={true}
          />
        </div>
      )}

      <div className="mt-10 flex justify-end">
        <Button
          disabled={importCocFileMutation.isLoading || !file?.fileName}
          type="button"
          className="w-full sm:w-auto min-w-[200px]"
          onClick={() => {
            setErrorLines([]);
            importCocFileMutation.mutate();
          }}
        >
          <span className="flex items-center justify-center gap-2">
            {importCocFileMutation.isLoading ? "Processing..." : "Process File"}
          </span>
        </Button>
      </div>
    </div>
  );
}
export default ShafafiyaPolicyImportForm;
